import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  HardDrive,
  ExternalLink,
  Search,
  Building2,
  Link2,
  Link2Off,
  Edit,
  Loader2,
  Save,
  CheckCircle,
  AlertCircle,
  FolderOpen,
  Eye,
  EyeOff,
  Lock,
  Copy,
  Check
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../lib/axios';

// =============================================
// GOOGLE DRIVE MANAGEMENT PAGE
// Gestion des dossiers Google Drive par client
// =============================================

const GoogleDriveManagementPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [saving, setSaving] = useState(null);
  
  // Edit Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [driveUrl, setDriveUrl] = useState('');
  const [drivePassword, setDrivePassword] = useState('');
  const [showPasswordInModal, setShowPasswordInModal] = useState(false);

  // Password reveal state - track which client's password is visible
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [copiedPasswords, setCopiedPasswords] = useState({});

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    withDrive: 0,
    withoutDrive: 0
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users/', { params: { role: 'client' } });
      const clientsData = response.data || [];
      setClients(clientsData);
      
      const withDrive = clientsData.filter(c => c.google_drive_url).length;
      setStats({
        total: clientsData.length,
        withDrive: withDrive,
        withoutDrive: clientsData.length - withDrive
      });
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleEditDrive = (client) => {
    setEditingClient(client);
    setDriveUrl(client.google_drive_url || '');
    setDrivePassword(client.google_drive_password || '');
    setShowPasswordInModal(false);
    setShowEditModal(true);
  };

  // Reveal password for 20 seconds max
  const revealPassword = useCallback((clientId) => {
    setVisiblePasswords(prev => ({ ...prev, [clientId]: true }));
    
    // Auto-hide after 20 seconds
    setTimeout(() => {
      setVisiblePasswords(prev => ({ ...prev, [clientId]: false }));
    }, 20000);
  }, []);

  // Hide password manually
  const hidePassword = useCallback((clientId) => {
    setVisiblePasswords(prev => ({ ...prev, [clientId]: false }));
  }, []);

  // Copy password to clipboard
  const copyPassword = useCallback(async (clientId, password) => {
    try {
      await navigator.clipboard.writeText(password);
      setCopiedPasswords(prev => ({ ...prev, [clientId]: true }));
      toast.success('Mot de passe copié !');
      
      // Reset copied state after 2 seconds
      setTimeout(() => {
        setCopiedPasswords(prev => ({ ...prev, [clientId]: false }));
      }, 2000);
    } catch (error) {
      toast.error('Erreur lors de la copie');
    }
  }, []);

  const handleSaveDriveUrl = async () => {
    if (!editingClient) return;
    
    setSaving(editingClient.id);
    try {
      await api.put(`/users/${editingClient.id}`, {
        google_drive_url: driveUrl || null,
        google_drive_password: drivePassword || null
      });
      
      // Update local state
      setClients(prev => prev.map(c => 
        c.id === editingClient.id ? { ...c, google_drive_url: driveUrl, google_drive_password: drivePassword } : c
      ));
      
      const withDrive = clients.filter(c => 
        c.id === editingClient.id ? driveUrl : c.google_drive_url
      ).length;
      setStats(prev => ({
        ...prev,
        withDrive: withDrive,
        withoutDrive: prev.total - withDrive
      }));
      
      toast.success('Lien Google Drive mis à jour !');
      setShowEditModal(false);
      setEditingClient(null);
      setDriveUrl('');
      setDrivePassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(null);
    }
  };

  const handleQuickSave = async (clientId, url) => {
    setSaving(clientId);
    try {
      await api.put(`/users/${clientId}`, {
        google_drive_url: url || null
      });
      
      setClients(prev => prev.map(c => 
        c.id === clientId ? { ...c, google_drive_url: url } : c
      ));
      
      toast.success('Lien mis à jour !');
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setSaving(null);
    }
  };

  const filteredClients = clients.filter(c => {
    const search = searchQuery.toLowerCase();
    return (
      c.company_name?.toLowerCase().includes(search) ||
      c.email?.toLowerCase().includes(search) ||
      `${c.first_name} ${c.last_name}`.toLowerCase().includes(search)
    );
  });

  const isValidDriveUrl = (url) => {
    if (!url) return false;
    return url.includes('drive.google.com') || url.includes('docs.google.com');
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 px-6 pb-6",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}
        style={{ paddingTop: "130px" }}
      >
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: theme.colors?.primary }}>
              <HardDrive className="w-6 h-6" />
              Dossiers Google Drive
            </h1>
            <p className="text-[#64748b] text-sm mt-1">
              Gérez les liens vers les dossiers partagés de vos clients
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="card">
            <CardContent className="p-4">
              <p className="text-xs text-[#64748b]">Total clients</p>
              <p className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>{stats.total}</p>
            </CardContent>
          </Card>
          <Card className="card">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-[#64748b]">Avec dossier Drive</p>
                <p className="text-2xl font-bold text-emerald-600">{stats.withDrive}</p>
              </div>
              <Link2 className="w-8 h-8 text-emerald-200" />
            </CardContent>
          </Card>
          <Card className="card">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-[#64748b]">Sans dossier</p>
                <p className="text-2xl font-bold text-amber-600">{stats.withoutDrive}</p>
              </div>
              <Link2Off className="w-8 h-8 text-amber-200" />
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="mb-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />
            <Input
              placeholder="Rechercher un client..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Table */}
        <Card className="card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Client / Entreprise</TableHead>
                <TableHead>Lien Google Drive</TableHead>
                <TableHead>Mot de passe</TableHead>
                <TableHead className="text-center">Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#64748b]" />
                  </TableCell>
                </TableRow>
              ) : filteredClients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-[#64748b]">
                    Aucun client trouvé
                  </TableCell>
                </TableRow>
              ) : (
                filteredClients.map(client => (
                  <TableRow key={client.id} className="group">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium" style={{ color: theme.colors?.primary }}>
                            {client.company_name || `${client.first_name} ${client.last_name}`}
                          </p>
                          <p className="text-xs text-[#64748b]">{client.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {client.google_drive_url ? (
                        <a 
                          href={client.google_drive_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 hover:underline max-w-xs truncate"
                        >
                          <FolderOpen className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">{client.google_drive_url}</span>
                          <ExternalLink className="w-3 h-3 flex-shrink-0" />
                        </a>
                      ) : (
                        <span className="text-[#94a3b8] text-sm italic">Non configuré</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {client.google_drive_password ? (
                        <div className="flex items-center gap-2">
                          {visiblePasswords[client.id] ? (
                            <>
                              <code className="px-2 py-1 bg-slate-100 rounded text-sm font-mono">
                                {client.google_drive_password}
                              </code>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => copyPassword(client.id, client.google_drive_password)}
                                title="Copier"
                              >
                                {copiedPasswords[client.id] ? (
                                  <Check className="w-3.5 h-3.5 text-emerald-600" />
                                ) : (
                                  <Copy className="w-3.5 h-3.5 text-[#64748b]" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => hidePassword(client.id)}
                                title="Masquer"
                              >
                                <EyeOff className="w-3.5 h-3.5 text-[#64748b]" />
                              </Button>
                              <span className="text-[10px] text-amber-600 animate-pulse">
                                Auto-masquage dans 20s
                              </span>
                            </>
                          ) : (
                            <>
                              <code className="px-2 py-1 bg-slate-100 rounded text-sm font-mono text-slate-400">
                                ••••••••
                              </code>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => revealPassword(client.id)}
                                title="Révéler (20s)"
                              >
                                <Eye className="w-3.5 h-3.5 text-[#64748b]" />
                              </Button>
                            </>
                          )}
                        </div>
                      ) : (
                        <span className="text-[#94a3b8] text-sm italic flex items-center gap-1">
                          <Lock className="w-3 h-3" />
                          Non défini
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {client.google_drive_url ? (
                        <Badge className="bg-emerald-100 text-emerald-700">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Partagé
                        </Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-700">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          À configurer
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleEditDrive(client)}
                        disabled={saving === client.id}
                      >
                        {saving === client.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Edit className="w-4 h-4 mr-1" />
                            {client.google_drive_url ? 'Modifier' : 'Ajouter'}
                          </>
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>

        {/* Edit Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <HardDrive className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                Dossier Google Drive
              </DialogTitle>
            </DialogHeader>
            
            {editingClient && (
              <div className="space-y-4 py-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm font-medium" style={{ color: theme.colors?.primary }}>
                    {editingClient.company_name || `${editingClient.first_name} ${editingClient.last_name}`}
                  </p>
                  <p className="text-xs text-[#64748b]">{editingClient.email}</p>
                </div>
                
                <div className="space-y-2">
                  <Label>Lien du dossier Google Drive partagé</Label>
                  <Input
                    value={driveUrl}
                    onChange={(e) => setDriveUrl(e.target.value)}
                    placeholder="https://drive.google.com/drive/folders/..."
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-[#64748b]">
                    Collez le lien de partage du dossier Google Drive contenant les fichiers du client
                  </p>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    Mot de passe d'accès (optionnel)
                  </Label>
                  <div className="relative">
                    <Input
                      type={showPasswordInModal ? "text" : "password"}
                      value={drivePassword}
                      onChange={(e) => setDrivePassword(e.target.value)}
                      placeholder="Mot de passe du dossier partagé"
                      className="font-mono text-sm pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                      onClick={() => setShowPasswordInModal(!showPasswordInModal)}
                    >
                      {showPasswordInModal ? (
                        <EyeOff className="w-4 h-4 text-[#64748b]" />
                      ) : (
                        <Eye className="w-4 h-4 text-[#64748b]" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-[#64748b]">
                    Si le dossier est protégé par un mot de passe, renseignez-le ici pour le partager avec le client
                  </p>
                </div>

                {driveUrl && (
                  <div className={cn(
                    "p-3 rounded-lg flex items-center gap-2 text-sm",
                    isValidDriveUrl(driveUrl) 
                      ? "bg-emerald-50 text-emerald-700" 
                      : "bg-amber-50 text-amber-700"
                  )}>
                    {isValidDriveUrl(driveUrl) ? (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        Lien Google Drive valide
                      </>
                    ) : (
                      <>
                        <AlertCircle className="w-4 h-4" />
                        Ce lien ne semble pas être un lien Google Drive
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditModal(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleSaveDriveUrl}
                disabled={saving}
                style={{ backgroundColor: theme.colors?.accent }}
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default GoogleDriveManagementPage;
